import { __decorate } from "tslib";
import React from 'react';
import classNames from 'classnames';
import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import Tree from 'choerodon-ui/lib/tree';
import TreeNode from './TreeNode';
import { DISABLED_FIELD, isSearchTextEmpty, MORE_KEY, Select } from '../select/Select';
import normalizeTreeNodes from './normalizeTreeNodes';
import autobind from '../_util/autobind';
import isIE from '../_util/isIE';
import { defaultRenderer } from '../tree';
import { getTreeNodes } from '../tree/util';
import { preventDefault } from '../_util/EventManager';
import { CheckedStrategy } from '../data-set/enum';
function recordIsDisabled(record) {
    return record.get(DISABLED_FIELD) || (record.get('__treeNodeProps') && record.get('__treeNodeProps').disableCheckbox);
}
let TreeSelect = class TreeSelect extends Select {
    get parentField() {
        return this.getProp('parentField') || 'parentValue';
    }
    get idField() {
        return this.getProp('idField') || this.valueField;
    }
    get options() {
        const { field, textField, valueField, idField, parentField, multiple, observableProps: { children, options }, getContextConfig, } = this;
        return (options ||
            (field && field.getOptions(this.record)) ||
            normalizeTreeNodes({
                textField,
                valueField,
                disabledField: DISABLED_FIELD,
                parentField,
                idField,
                multiple,
                children,
                getConfig: getContextConfig,
            }));
    }
    get treeData() {
        return this.optionsWithCombo.filter(r => !r.parent);
    }
    get selectedKeys() {
        const { idField } = this;
        return this.options.reduce((array, r) => this.isSelected(r) ? array.concat(String(r.get(idField))) : array, []);
    }
    handleSearch(text) {
        if (!isSearchTextEmpty(text)) {
            const { options } = this;
            const { idField } = options.props;
            this.expandedKeys = options.map((r) => String(r.get(idField)));
        }
    }
    handleExpand(keys) {
        this.expandedKeys = keys;
    }
    get multiple() {
        return !!this.getProp('multiple') || !!this.props.treeCheckable;
    }
    handleTreeSelect(_e, { node }) {
        const { record, disabled, key } = node;
        if (key === MORE_KEY) {
            const { options } = this;
            options.queryMore(options.currentPage + 1);
        }
        else if (!disabled) {
            const { multiple } = this;
            if (multiple) {
                if (this.isSelected(record)) {
                    this.unChoose(record);
                }
                else {
                    this.choose(record);
                }
            }
            else {
                this.choose(record);
            }
        }
    }
    handleTreeCheck(_e, { node }) {
        const { record, disabled, key, disableCheckbox } = node;
        const { valueField } = this;
        if (key === MORE_KEY) {
            const { options } = this;
            options.queryMore(options.currentPage + 1);
        }
        else if (!disabled && !disableCheckbox) {
            const { multiple } = this;
            if (multiple) {
                const disabledChildRecords = [];
                const records = record.treeReduce((array, r) => {
                    if (recordIsDisabled(r)) {
                        disabledChildRecords.push(r);
                        return array;
                    }
                    return this.isSelected(r) || r.parents.some(parent => disabledChildRecords.includes(parent)) ? array : array.concat(r);
                }, []);
                const parents = [];
                record.parents.every((parent, index) => {
                    if (recordIsDisabled(parent)) {
                        return false;
                    }
                    const { children } = parent;
                    if (children && children.every(child => {
                        return recordIsDisabled(child) || (index === 0 ? records.includes(child) : parents.includes(child)) || this.isSelected(child);
                    })) {
                        parents.push(parent);
                        return true;
                    }
                    return false;
                });
                const showCheckedStrategy = this.getProp('showCheckedStrategy');
                if (showCheckedStrategy === CheckedStrategy.SHOW_ALL) {
                    if (this.isSelected(record)) {
                        const unChooseRecords = record.treeReduce((array, r) => {
                            return this.isSelected(r) && !recordIsDisabled(r) && r.parents.every(parent => !disabledChildRecords.includes(parent)) ? array.concat(r) : array;
                        }, []);
                        const disabledParentRecords = [];
                        const unChooseParents = record.parents.filter(parent => {
                            if (recordIsDisabled(parent)) {
                                disabledParentRecords.push(parent, ...parent.parents);
                                return false;
                            }
                            return this.isSelected(parent) && !disabledParentRecords.includes(parent);
                        });
                        this.unChoose(unChooseRecords.concat(unChooseParents));
                    }
                    else {
                        this.choose(records.concat(parents));
                    }
                }
                else if (showCheckedStrategy === CheckedStrategy.SHOW_PARENT) {
                    const disabledParentRecords = [];
                    if (this.isSelected(record) ||
                        record.parents.some(parent => {
                            if (recordIsDisabled(parent)) {
                                disabledParentRecords.push(parent, ...parent.parents);
                            }
                            return this.isSelected(parent) && !recordIsDisabled(parent) && !disabledParentRecords.includes(parent);
                        })) {
                        const recordFirstDisabledParent = record.parents.find(parent => recordIsDisabled(parent));
                        const selectedRecords = this.options.filter(option => {
                            if (recordIsDisabled(option)) {
                                return this.isSelected(option);
                            }
                            if (key === option.get(valueField) || (option.children && option.children.includes(record))) {
                                return false;
                            }
                            const firstDisabledParent = option.parents.find(parent => recordIsDisabled(parent));
                            if ((firstDisabledParent && !record.parents.includes(firstDisabledParent)) ||
                                (recordFirstDisabledParent && !option.parents.includes(recordFirstDisabledParent)) ||
                                (firstDisabledParent && recordFirstDisabledParent &&
                                    (firstDisabledParent.parents.includes(recordFirstDisabledParent) || recordFirstDisabledParent.parents.includes(firstDisabledParent)))) {
                                return this.isSelected(option);
                            }
                            if (firstDisabledParent && recordFirstDisabledParent === firstDisabledParent) {
                                if (record.parents.includes(option) || option.parents.includes(record)) {
                                    return false;
                                }
                                if (record.parents.some(recordParent => recordParent === option.parent && recordParent.parents.includes(firstDisabledParent)) &&
                                    (option.parents.some(parent => this.isSelected(parent) && parent.parents.includes(firstDisabledParent)) || this.isSelected(option))) {
                                    return true;
                                }
                                if (option.parents.some(optionParent => record.parents.includes(optionParent) && optionParent.parents.includes(firstDisabledParent)) &&
                                    this.isSelected(option)) {
                                    return true;
                                }
                                return this.isSelected(option);
                            }
                            if (record.parents.includes(option) || option.parents.includes(record)) {
                                return false;
                            }
                            if (option.parent && record.parents.includes(option.parent) && (option.parents.some(parent => this.isSelected(parent)) || this.isSelected(option))) {
                                return true;
                            }
                            if (option.parents.some(optionParent => record.parents.includes(optionParent)) && this.isSelected(option)) {
                                return true;
                            }
                            return false;
                        });
                        this.setValue(selectedRecords.map(this.processRecordToObject, this));
                    }
                    else {
                        const preSelected = this.options.filter(option => this.isSelected(option)).concat(record, parents);
                        const selectedRecords = preSelected.filter((child) => {
                            return recordIsDisabled(child) || (child.parent && recordIsDisabled(child.parent)) || !(child.parent && preSelected.includes(child.parent));
                        });
                        this.setValue(selectedRecords.map(this.processRecordToObject, this));
                    }
                }
                else if (showCheckedStrategy === 'SHOW_CHILD') {
                    if (!record.parent) {
                        const disabledRootRecords = this.options.filter(option => recordIsDisabled(option) && option.parents.every(optionParent => !recordIsDisabled(optionParent)));
                        if (disabledRootRecords &&
                            this.options.every(option => recordIsDisabled(option) || option.parents.some(optionParent => disabledRootRecords.includes(optionParent)) ||
                                (this.isSelected(option) && (!option.children || (!!option.children && option.children.every(child => disabledRootRecords.includes(child))))) ||
                                (!!option.children && option.children.some(optionChild => !recordIsDisabled(optionChild))))) {
                            const unchooseRecords = this.options.filter(option => !recordIsDisabled(option) &&
                                option.parents.every(optionParent => !recordIsDisabled(optionParent)) && this.isSelected(option));
                            this.unChoose(unchooseRecords);
                        }
                        else if (!disabledRootRecords && this.options.every(option => (!option.children && this.isSelected(option)) || !!option.children)) {
                            this.unChooseAll();
                        }
                        else {
                            this.choose(records.filter(childRecord => !childRecord.children || childRecord.children.every(childChild => recordIsDisabled(childChild))));
                        }
                        return;
                    }
                    if (this.isSelected(record) && (!record.children || record.children.every(recordChild => recordIsDisabled(recordChild)))) {
                        this.unChoose(record);
                    }
                    else if (!this.isSelected(record) && record.children && record.parent) {
                        const allLeafChildren = [];
                        const selectedChildren = [];
                        record.treeReduce((_, r) => {
                            if (r.parents.every(parent => !disabledChildRecords.includes(parent)) &&
                                ((!recordIsDisabled(r) && !r.children) || (r.children && r.children.every(recordChild => recordIsDisabled(recordChild))))) {
                                allLeafChildren.push(r);
                                if (this.isSelected(r)) {
                                    selectedChildren.push(r);
                                }
                            }
                            return [];
                        }, []);
                        if (allLeafChildren.length === selectedChildren.length && selectedChildren.length !== 0) {
                            this.unChoose(selectedChildren);
                        }
                        else {
                            this.choose(records.filter(chooseRecord => !chooseRecord.children || chooseRecord.children.every(childChild => recordIsDisabled(childChild))));
                        }
                    }
                    else {
                        this.choose(records.filter(chooseRecord => !chooseRecord.children || chooseRecord.children.every(childChild => recordIsDisabled(childChild))));
                    }
                }
            }
            else {
                this.choose(record);
            }
        }
    }
    handleTreeNode(e) {
        const { onOption } = this.props;
        const props = onOption(e) || {};
        props.className = classNames(props.className, `${this.getMenuPrefixCls()}-item`);
        return props;
    }
    handleLoadData(event) {
        const { loadData } = this.props;
        const dataSet = this.options;
        const promises = [];
        if (dataSet) {
            const { record } = event.props;
            promises.push(dataSet.queryMoreChild(record, dataSet.currentPage));
        }
        if (loadData) {
            promises.push(loadData(event));
        }
        return Promise.all(promises);
    }
    renderSelectAll() {
        // noop
    }
    getMenu(menuProps = {}) {
        const { options, } = this;
        const menuPrefixCls = this.getMenuPrefixCls();
        const { disabled: menuDisabled, textField, selectedKeys, expandedKeys, multiple, text, props: { dropdownMenuStyle, optionRenderer = defaultRenderer, optionsFilter, treeDefaultExpandAll, treeDefaultExpandedKeys, treeCheckable, async, loadData, }, } = this;
        /**
         * fixed when ie the scroll width would cover the item width
         */
        const IeMenuStyle = !this.dropdownMatchSelectWidth && isIE() ? { padding: '.08rem' } : {};
        const treeData = getTreeNodes(options, this.treeData, optionRenderer, this.handleTreeNode, async || !!loadData, textField, optionsFilter, this.matchRecordBySearch, text);
        if (!treeData || !treeData.length) {
            return (React.createElement("div", { className: menuPrefixCls },
                React.createElement("div", { className: `${menuPrefixCls}-item ${menuPrefixCls}-item-disabled` }, this.loading ? ' ' : this.getNotFoundContent())));
        }
        if (options.paging && options.currentPage < options.totalPage) {
            treeData.push({
                key: MORE_KEY,
                eventKey: MORE_KEY,
                title: this.getPagingOptionContent(),
                className: `${menuPrefixCls}-item ${menuPrefixCls}-item-more`,
                isLeaf: true,
            });
        }
        const props = {};
        if (expandedKeys) {
            props.expandedKeys = expandedKeys.slice();
        }
        return (React.createElement(Tree, Object.assign({ ref: this.saveMenu, ripple: true, disabled: menuDisabled, onMouseDown: preventDefault, onSelect: treeCheckable ? this.handleTreeCheck : this.handleTreeSelect, onCheck: this.handleTreeCheck, onExpand: this.handleExpand, style: { ...IeMenuStyle, ...dropdownMenuStyle }, selectable: true, focusable: false, treeData: treeData, defaultExpandAll: treeDefaultExpandAll, defaultExpandedKeys: treeDefaultExpandedKeys, selectedKeys: selectedKeys, checkedKeys: selectedKeys, checkable: treeCheckable, className: menuPrefixCls, multiple: multiple, loadData: async ? this.handleLoadData : loadData }, props, menuProps)));
    }
};
TreeSelect.displayName = 'TreeSelect';
TreeSelect.defaultProps = {
    ...Select.defaultProps,
    suffixCls: 'tree-select',
    dropdownMatchSelectWidth: false,
    showCheckedStrategy: 'SHOW_ALL',
    reverse: false,
};
TreeSelect.TreeNode = TreeNode;
__decorate([
    observable
], TreeSelect.prototype, "expandedKeys", void 0);
__decorate([
    computed
], TreeSelect.prototype, "parentField", null);
__decorate([
    computed
], TreeSelect.prototype, "idField", null);
__decorate([
    computed
], TreeSelect.prototype, "options", null);
__decorate([
    computed
], TreeSelect.prototype, "treeData", null);
__decorate([
    computed
], TreeSelect.prototype, "selectedKeys", null);
__decorate([
    action
], TreeSelect.prototype, "handleSearch", null);
__decorate([
    autobind,
    action
], TreeSelect.prototype, "handleExpand", null);
__decorate([
    computed
], TreeSelect.prototype, "multiple", null);
__decorate([
    autobind
], TreeSelect.prototype, "handleTreeSelect", null);
__decorate([
    autobind
], TreeSelect.prototype, "handleTreeCheck", null);
__decorate([
    autobind
], TreeSelect.prototype, "handleTreeNode", null);
__decorate([
    autobind
], TreeSelect.prototype, "handleLoadData", null);
__decorate([
    autobind
], TreeSelect.prototype, "getMenu", null);
TreeSelect = __decorate([
    observer
], TreeSelect);
export default TreeSelect;
